import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// Types for profiles
export interface AdminProfileFull {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  agency_name: string | null;
  agency_id: string | null;
  iata_number: string | null;
  iata_verified: boolean;
  account_status: 'pending' | 'approved' | 'rejected';
  role: string | null;
  phone: string | null;
  country_code: string | null;
  bio: string | null;
  specialties: string[] | null;
  top_countries: string[] | null;
  profile_picture_url: string | null;
  created_at: string;
  updated_at: string;
}

// Types for agencies
export interface Agency {
  agency_id: string;
  name: string;
  description: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  city: string | null;
  country: string | null;
  logo_url: string | null;
  is_active: boolean;
  max_agents: number;
  subscription_tier: 'basic' | 'pro' | 'enterprise';
  subscription_status: 'active' | 'trial' | 'suspended' | 'cancelled';
  created_at: string;
  agent_count: number;
  style_settings: StyleSettings | null;
  primary_color: string | null;
  subdomain: string | null;
  address_line1: string | null;
  address_line2: string | null;
  state_province: string | null;
  postal_code: string | null;
}

export interface StyleSettings {
  colors?: {
    info?: string;
    error?: string;
    accent?: string;
    infoBg?: string;
    overlay?: string;
    primary?: string;
    success?: string;
    warning?: string;
    accentTan?: string;
    textMuted?: string;
    accentGold?: string;
    background?: string;
    textStrong?: string;
    borderLight?: string;
    headingText?: string;
    successDark?: string;
    accentPurple?: string;
    borderStrong?: string;
    modalSidebar?: string;
    successLight?: string;
    backgroundAlt?: string;
    borderDefault?: string;
    secondaryText?: string;
    formBackground?: string;
    backgroundHover?: string;
    modalBackground?: string;
    imagePlaceholder?: string;
    textColor?: string;
  };
  buttons?: {
    buttonColor?: string;
    buttonHoverDark?: string;
    buttonTextColor?: string;
    buttonHoverColor?: string;
    buttonDangerColor?: string;
    buttonSuccessColor?: string;
  };
  branding?: {
    brandName?: string;
  };
  typography?: {
    fontFamily?: string;
  };
  eventColors?: {
    other?: string;
    dining?: string;
    hotels?: string;
    cruises?: string;
    flights?: string;
    insurance?: string;
    activities?: string;
    transportation?: string;
  };
}

export interface CreateAgencyInput {
  name: string;
  description?: string;
  email?: string;
  phone?: string;
  website?: string;
  city?: string;
  country?: string;
  max_agents?: number;
  subscription_tier?: 'basic' | 'pro' | 'enterprise';
  subscription_status?: 'active' | 'trial' | 'suspended' | 'cancelled';
  subdomain?: string;
  address_line1?: string;
  address_line2?: string;
  state_province?: string;
  postal_code?: string;
  style_settings?: StyleSettings;
  primary_color?: string;
}

export interface UpdateAgencyInput {
  name?: string;
  description?: string;
  email?: string;
  phone?: string;
  website?: string;
  city?: string;
  country?: string;
  is_active?: boolean;
  max_agents?: number;
  subscription_tier?: 'basic' | 'pro' | 'enterprise';
  subscription_status?: 'active' | 'trial' | 'suspended' | 'cancelled';
  subdomain?: string;
  address_line1?: string;
  address_line2?: string;
  state_province?: string;
  postal_code?: string;
  style_settings?: StyleSettings;
  primary_color?: string;
  logo_url?: string;
}

export interface UpdateProfileInput {
  first_name?: string;
  last_name?: string;
  agency_name?: string;
  agency_id?: string;
  iata_number?: string;
  iata_verified?: boolean;
  account_status?: 'pending' | 'approved' | 'rejected';
  role?: string;
  phone?: string;
  country_code?: string;
  bio?: string;
}

// Types for requests
export interface AdminRequest {
  id: string;
  agent_id: string;
  agency_id: string | null;
  date: string;
  request: string;
  resolved: boolean;
  category: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  agent_first_name: string | null;
  agent_last_name: string | null;
  agent_email: string | null;
  agency_name: string | null;
}

interface UseSuperAdminReturn {
  isSuperAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  // Profile management
  getAllProfiles: () => Promise<AdminProfileFull[]>;
  updateProfile: (userId: string, updates: UpdateProfileInput) => Promise<boolean>;
  approveProfile: (userId: string) => Promise<boolean>;
  rejectProfile: (userId: string) => Promise<boolean>;
  // Agency management
  getAllAgencies: () => Promise<Agency[]>;
  createAgency: (input: CreateAgencyInput) => Promise<string>;
  updateAgency: (agencyId: string, updates: UpdateAgencyInput) => Promise<boolean>;
  // Request management
  getAllRequests: () => Promise<AdminRequest[]>;
  updateRequestResolved: (requestId: string, resolved: boolean) => Promise<boolean>;
}

/**
 * Hook to check if current user is a super admin and provide admin functions
 */
export function useSuperAdmin(): UseSuperAdminReturn {
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check super admin status on mount
  useEffect(() => {
    async function checkSuperAdminStatus() {
      try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          setIsSuperAdmin(false);
          setIsLoading(false);
          return;
        }

        // Check if user exists in super_admins table
        const { data, error: queryError } = await supabase
          .from('super_admins')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (queryError) {
          // PGRST116 is "no rows returned" which is expected for non-admins
          if (queryError.code === 'PGRST116') {
            setIsSuperAdmin(false);
          } else {
            // Other errors might be RLS blocking the query (also means not admin)
            setIsSuperAdmin(false);
          }
        } else {
          setIsSuperAdmin(!!data);
        }
      } catch (err) {
        console.error('Error checking super admin status:', err);
        setError('Failed to verify admin status');
        setIsSuperAdmin(false);
      } finally {
        setIsLoading(false);
      }
    }

    checkSuperAdminStatus();
  }, []);

  // Get all profiles with full details
  const getAllProfiles = useCallback(async (): Promise<AdminProfileFull[]> => {
    if (!isSuperAdmin) {
      throw new Error('Access denied: Super admin privileges required');
    }

    const { data, error: rpcError } = await supabase.rpc('get_all_profiles_admin_full');

    if (rpcError) {
      console.error('Error fetching profiles:', rpcError);
      throw new Error(rpcError.message);
    }

    return (data || []) as AdminProfileFull[];
  }, [isSuperAdmin]);

  // Update a profile
  const updateProfile = useCallback(async (
    userId: string,
    updates: UpdateProfileInput
  ): Promise<boolean> => {
    if (!isSuperAdmin) {
      throw new Error('Access denied: Super admin privileges required');
    }

    const { data, error: rpcError } = await supabase.rpc('admin_update_profile', {
      target_user_id: userId,
      p_first_name: updates.first_name ?? null,
      p_last_name: updates.last_name ?? null,
      p_agency_name: updates.agency_name ?? null,
      p_agency_id: updates.agency_id ?? null,
      p_iata_number: updates.iata_number ?? null,
      p_iata_verified: updates.iata_verified ?? null,
      p_account_status: updates.account_status ?? null,
      p_role: updates.role ?? null,
      p_phone: updates.phone ?? null,
      p_country_code: updates.country_code ?? null,
      p_bio: updates.bio ?? null,
    });

    if (rpcError) {
      console.error('Error updating profile:', rpcError);
      throw new Error(rpcError.message);
    }

    return data as boolean;
  }, [isSuperAdmin]);

  // Convenience function to approve a profile
  const approveProfile = useCallback(async (userId: string): Promise<boolean> => {
    return updateProfile(userId, { account_status: 'approved' });
  }, [updateProfile]);

  // Convenience function to reject a profile
  const rejectProfile = useCallback(async (userId: string): Promise<boolean> => {
    return updateProfile(userId, { account_status: 'rejected' });
  }, [updateProfile]);

  // Get all agencies
  const getAllAgencies = useCallback(async (): Promise<Agency[]> => {
    if (!isSuperAdmin) {
      throw new Error('Access denied: Super admin privileges required');
    }

    const { data, error: rpcError } = await supabase.rpc('get_all_agencies_admin');

    if (rpcError) {
      console.error('Error fetching agencies:', rpcError);
      throw new Error(rpcError.message);
    }

    return (data || []) as Agency[];
  }, [isSuperAdmin]);

  // Create a new agency
  const createAgency = useCallback(async (input: CreateAgencyInput): Promise<string> => {
    if (!isSuperAdmin) {
      throw new Error('Access denied: Super admin privileges required');
    }

    const { data, error: rpcError } = await supabase.rpc('admin_create_agency', {
      p_name: input.name,
      p_description: input.description || null,
      p_email: input.email || null,
      p_phone: input.phone || null,
      p_website: input.website || null,
      p_city: input.city || null,
      p_country: input.country || null,
      p_max_agents: input.max_agents ?? 10,
      p_subscription_tier: input.subscription_tier ?? 'basic',
      p_subscription_status: input.subscription_status ?? 'active',
      p_subdomain: input.subdomain || null,
      p_address_line1: input.address_line1 || null,
      p_address_line2: input.address_line2 || null,
      p_state_province: input.state_province || null,
      p_postal_code: input.postal_code || null,
      p_style_settings: input.style_settings ?? null,
      p_primary_color: input.primary_color || null,
    });

    if (rpcError) {
      console.error('Error creating agency:', rpcError);
      throw new Error(rpcError.message);
    }

    return data as string;
  }, [isSuperAdmin]);

  // Update an agency
  const updateAgency = useCallback(async (
    agencyId: string,
    updates: UpdateAgencyInput
  ): Promise<boolean> => {
    if (!isSuperAdmin) {
      throw new Error('Access denied: Super admin privileges required');
    }

    const { data, error: rpcError } = await supabase.rpc('admin_update_agency', {
      p_agency_id: agencyId,
      p_name: updates.name || null,
      p_description: updates.description || null,
      p_email: updates.email || null,
      p_phone: updates.phone || null,
      p_website: updates.website || null,
      p_city: updates.city || null,
      p_country: updates.country || null,
      p_is_active: updates.is_active ?? null,
      p_max_agents: updates.max_agents ?? null,
      p_subscription_tier: updates.subscription_tier || null,
      p_subscription_status: updates.subscription_status || null,
      p_subdomain: updates.subdomain || null,
      p_address_line1: updates.address_line1 || null,
      p_address_line2: updates.address_line2 || null,
      p_state_province: updates.state_province || null,
      p_postal_code: updates.postal_code || null,
      p_style_settings: updates.style_settings ?? null,
      p_primary_color: updates.primary_color || null,
      p_logo_url: updates.logo_url || null,
    });

    if (rpcError) {
      console.error('Error updating agency:', rpcError);
      throw new Error(rpcError.message);
    }

    return data as boolean;
  }, [isSuperAdmin]);

  // Get all requests with agent and agency info
  const getAllRequests = useCallback(async (): Promise<AdminRequest[]> => {
    if (!isSuperAdmin) {
      throw new Error('Access denied: Super admin privileges required');
    }

    const { data, error: queryError } = await supabase
      .from('requests')
      .select(`
        id,
        agent_id,
        agency_id,
        date,
        request,
        resolved,
        category,
        created_at,
        updated_at,
        profiles:agent_id (
          first_name,
          last_name,
          email
        ),
        agencies:agency_id (
          name
        )
      `)
      .order('date', { ascending: false });

    if (queryError) {
      console.error('Error fetching requests:', queryError);
      throw new Error(queryError.message);
    }

    // Transform the data to flatten the joined fields
    return (data || []).map((r: Record<string, unknown>) => ({
      id: r.id as string,
      agent_id: r.agent_id as string,
      agency_id: r.agency_id as string | null,
      date: r.date as string,
      request: r.request as string,
      resolved: r.resolved as boolean,
      category: r.category as string | null,
      created_at: r.created_at as string,
      updated_at: r.updated_at as string,
      agent_first_name: (r.profiles as Record<string, unknown> | null)?.first_name as string | null ?? null,
      agent_last_name: (r.profiles as Record<string, unknown> | null)?.last_name as string | null ?? null,
      agent_email: (r.profiles as Record<string, unknown> | null)?.email as string | null ?? null,
      agency_name: (r.agencies as Record<string, unknown> | null)?.name as string | null ?? null,
    }));
  }, [isSuperAdmin]);

  // Update request resolved status
  const updateRequestResolved = useCallback(async (
    requestId: string,
    resolved: boolean
  ): Promise<boolean> => {
    if (!isSuperAdmin) {
      throw new Error('Access denied: Super admin privileges required');
    }

    const { error: updateError } = await supabase
      .from('requests')
      .update({ resolved })
      .eq('id', requestId);

    if (updateError) {
      console.error('Error updating request:', updateError);
      throw new Error(updateError.message);
    }

    return true;
  }, [isSuperAdmin]);

  return {
    isSuperAdmin,
    isLoading,
    error,
    getAllProfiles,
    updateProfile,
    approveProfile,
    rejectProfile,
    getAllAgencies,
    createAgency,
    updateAgency,
    getAllRequests,
    updateRequestResolved,
  };
}
